import EventHandler from '@pixelunion/events';
import { transition } from '@pixelunion/animations';
import rimg from '@pixelunion/rimg-shopify';
import themeUtils from '../themeUtils';
import SiteOverlay from './SiteOverlay';

export default class PredictiveSearch {
  constructor({ el }) {
    this.el = el;
    this.events = new EventHandler();
    this.siteOverlay = new SiteOverlay();

    this.input = this.el.querySelector('[data-search-input]');
    this.results = this.el.querySelector('[data-search-results]');
    this.resultsContainer = this.el.querySelector('[data-search-results-container]');
    this.animation = transition({ el: this.resultsContainer });

    this.isOpen = false;

    this.events.register(window, 'keyup', e => this._onKeyUp(e));
    this.events.register(window, 'resize', themeUtils.debounce(() => this._onResize(), 250));
    this.events.register(this.el, 'submit', e => this._onSubmit(e));
    this.events.register(this.input, 'input', themeUtils.debounce(() => this._onChange(), 250));
    this.events.register(this.input, 'focus', () => this._onFocus());
    this.events.register(this.input, 'keydown', e => this._onKeydown(e));

    this.events.register(document.body, 'click', e => {
      if (!e.target.closest('[data-search-form]')) {
        this._close();
      }
    });
  }

  _getQuery() {
    return this.input.value.trim();
  }

  _onChange() {
    const searchTerm = this._getQuery();

    if (!searchTerm.length) {
      this._close();
      return;
    }

    this._getSearchResults(searchTerm);
  }

  _onBlur() {
    this._close();
  }

  _onFocus() {
    const searchTerm = this._getQuery();

    if (!searchTerm.length) return;

    this._getSearchResults(searchTerm);
  }

  _onSubmit(e) {
    if (!this._getQuery().length) e.preventDefault();
  }

  _onKeyUp(e) {
    if (e.code === 'Escape') {
      this.input.value = '';
      this._close();
    }
  }

  _onKeydown(e) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (e.code === 'ArrowUp' || e.code === 'ArrowDown') {
      e.preventDefault();
    }
  }

  _onResize() {
    this._resizeSearchResults();

    if (themeUtils.isLessThanLarge()) {
      this._close();
    }
  }

  _getSearchResults(searchTerm) {
    this.resultsContainer.setAttribute('data-loading', 'true');

    fetch(`${window.Theme.routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&section_id=predictive-search`)
      .then(response => {
        if (!response.ok) {
          const error = new Error(response.status);
          this._close();
          throw error;
        }

        return response.text();
      })
      .then(text => {
        const resultsMarkup = new DOMParser().parseFromString(text, 'text/html').querySelector('#shopify-section-predictive-search').innerHTML;
        this._renderSearchResults(resultsMarkup);
        rimg.watch(this.results);
      })
      .catch(error => {
        this._close();
        throw error;
      });
  }

  _registerEvents() {
    this.searchButton = this.el.querySelector('[data-predictive-search-button]');
    this.events.register(this.searchButton, 'blur', () => this._onBlur());
  }

  _resizeSearchResults() {
    const offset = 30;
    const position = this.input.getBoundingClientRect();
    const maxHeight = window.innerHeight - position.bottom - offset;

    this.resultsContainer.style.setProperty('--results-container-height', `${maxHeight}px`);
  }

  _renderSearchResults(resultsMarkup) {
    this.resultsContainer.setAttribute('data-loading', 'false');
    this.results.innerHTML = resultsMarkup;

    this._open();
    this._registerEvents();
    this._resizeSearchResults();
  }

  _open() {
    if (this.isOpen) return;
    this.isOpen = true;

    this.animation.animateTo('open')
      .then(() => {
        document.body.classList.add('scroll-locked');
        this.siteOverlay.open();
      });
  }

  _close() {
    if (!this.isOpen) return;

    this.animation.animateTo('closed')
      .then(() => {
        this.isOpen = false;
        document.body.classList.remove('scroll-locked');
        this.siteOverlay.close();
      });
  }
}
